import { array, boolean, mixed, number, object, string } from "yup";
import { PortfolioInput, PortfolioIntegrationType } from "@graphql/api";
import { emailRequired } from "@covertree/utils";

export interface PortfolioConfigurationFormValues
  extends Omit<
      PortfolioInput,
      | "sendInitialDocumentUploadReminders"
      | "state"
      | "secondDocumentReminderDelayInDays"
      | "thirdDocumentReminderDelayInDays"
    >,
    Partial<
      Pick<
        PortfolioInput,
        "secondDocumentReminderDelayInDays" | "thirdDocumentReminderDelayInDays"
      >
    > {
  disableSendInitialDocumentUploadReminders: boolean;
  disableSendSecondDocumentReminder: boolean;
  disableSendThirdDocumentReminder: boolean;
  state: { label: string; value: string };
}

// Mirrors `uuid` validate() used by the backend; yup's string().uuid() rejects v6–v8 (incl. UUIDv7).
export const PORTFOLIO_ID_UUID_REGEX =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/i;
export const portfolioConfigurationFormInitialValues = {
  createResidentHubspotLeadOnImport: true,
  delayToStartUnderReviewTimePeriodAfterCancellationOrExpirationInDays: 0,
  disableSendInitialDocumentUploadReminders: false,
  emailAddresses: [],
  firstDocumentReminderDelayInDays: 0,
  id: "",
  minUnderReviewTimePeriodInDays: 45,
  disableSendSecondDocumentReminder: false,
  secondDocumentReminderDelayInDays: 25,
  disableSendThirdDocumentReminder: false,
  thirdDocumentReminderDelayInDays: 35,
  syncAddressData: true,
} as unknown as PortfolioConfigurationFormValues;

export const validationSchemaForPortfolioConfigurationForm = object({
  addressLine1: string().required("Address Line 1 is required"),
  addressLine2: string().optional(),
  city: string().required("City is required"),
  county: string().required("County is required"),
  createResidentHubspotLeadOnImport: boolean().required(
    "Configuration for Hubspot is required"
  ),
  delayToStartUnderReviewTimePeriodAfterCancellationOrExpirationInDays: number()
    .min(0, "Delay days has to be at least 0")
    .typeError("Delay days must be a number")
    .required("Delay configuration is required"),
  disableSendInitialDocumentUploadReminders: boolean().required(),
  emailAddresses: array()
    .of(emailRequired())
    .min(1, "At least one email is required")
    .required("At least one email is required"),
  firstDocumentReminderDelayInDays: number()
    .min(0, "First Document Reminder days has to be at least 0")
    .typeError("First Document Reminder days must be a number")
    .when(
      [
        "sendSecondDocumentReminder",
        "secondDocumentReminderDelayInDays",
        "minUnderReviewTimePeriodInDays",
      ],
      ([sendSecond, secondDelay, underReviewDays], schema) =>
        sendSecond
          ? schema.max(secondDelay - 1)
          : schema.max(underReviewDays - 1)
    )
    .optional(),
  id: string().trim().optional().matches(PORTFOLIO_ID_UUID_REGEX, {
    excludeEmptyString: true,
    message: "Portfolio ID must be a valid UUID",
  }),
  integrationId: string().required("Integration ID is required"),
  integrationType: mixed<PortfolioIntegrationType>()
    .oneOf(Object.values(PortfolioIntegrationType))
    .required("Integration type is required"),
  syncAddressData: boolean().required("Sync address data is required"),
  managersName: string().required("Manager's names are required"),
  minUnderReviewTimePeriodInDays: number()
    .typeError("Minimum Under Review days must be a number")
    .min(0, "Minimum Under Review days has to be at least 0")
    .required("Minimum Under Review days is required"),
  name: string()
    .minWords(1, "Portfolio name has to have at least one word")
    .required("Portfolio Name is required"),
  disableSendSecondDocumentReminder: boolean().required(),
  secondDocumentReminderDelayInDays: number()
    .min(1)
    .when(["minUnderReviewTimePeriodInDays"], ([underReviewPeriod], schema) =>
      schema.max(underReviewPeriod - 1, ({ max }) => `Must be less than ${max}`)
    )
    .when("disableSendSecondDocumentReminder", ([val], schema) =>
      !val
        ? schema.required("Delay to send second reminder is required")
        : string().length(0)
    ),
  state: object({
    label: string().required(),
    value: string().required(),
  })
    .typeError("State is required")
    .required("State is required"),
  disableSendThirdDocumentReminder: boolean()
    .when("disableSendSecondDocumentReminder", ([val], schema) =>
      val
        ? schema.isTrue(
            "Once second reminder is disabled, third has to be disabled too"
          )
        : schema
    )
    .required(),
  thirdDocumentReminderDelayInDays: number()
    .when(
      [
        "disableSendSecondDocumentReminder",
        "secondDocumentReminderDelayInDays",
        "minUnderReviewTimePeriodInDays",
      ],
      ([sendSecond, secondDelay, underReviewPeriod], schema) =>
        !sendSecond
          ? schema
              .min(secondDelay + 1, ({ min }) => `Must be more than ${min}`)
              .max(
                underReviewPeriod - 1,
                ({ max }) => `Must be less than ${max}`
              )
          : schema.max(
              underReviewPeriod - 1,
              ({ max }) => `Must be less than ${max}`
            )
    )
    .when("disableSendThirdDocumentReminder", ([val], schema) =>
      !val
        ? schema.required("Delay to send third reminder is required")
        : string().length(0)
    ),
  zipCode: string()
    .matches(/^\d{5}(-\d{4})?$/, "Zip Code must be a valid format")
    .required("Zip Code is required"),
});
